/**
 * Copies `text` to the clipboard. Prefers the modern async Clipboard API and
 * falls back to a transient hidden textarea + `document.execCommand('copy')`
 * for browsers / contexts where `navigator.clipboard` is unavailable or
 * permission-blocked.
 *
 * @returns `true` if the write succeeded by either path, `false` otherwise.
 */
export const copyToClipboard = async(text: string): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to execCommand fallback.
    }
  }
  if (typeof document === 'undefined') return false;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};
